import { useState } from 'react';

/**
 * VenueMap — a static map image with WIC-branded numbered pins overlaid,
 * plus a legend below. Pins are positioned as percentages of the image
 * box so they survive any resize. Swap the image and the pin x/y if the
 * underlying screenshot changes.
 *
 * Map images live in public/images/ (same-origin — never cross-origin from
 * westerncon.com; see PROJECT_BRIEF §3 landmine #7).
 */

export interface MapPin {
  /** Number shown in the pin and the legend. Two spots can share a number
   *  (e.g. two parking lots both labelled "2"). */
  num: number;
  /** Horizontal position, 0–100, as a percent of image width. */
  x: number;
  /** Vertical position, 0–100, as a percent of image height. */
  y: number;
}

export interface LegendItem {
  num: number;
  label: string;
  detail?: string;
}

export interface VenueMapProps {
  eyebrow: string;        // e.g. "Morning · Golf"
  title: string;         // e.g. "Riverdale Dunes"
  imageSrc: string;      // e.g. "/images/map-golf.png"
  imageAlt: string;
  pins: MapPin[];
  legend: LegendItem[];
}

const WIC_RED = '#C0392B';

export default function VenueMap({
  eyebrow,
  title,
  imageSrc,
  imageAlt,
  pins,
  legend,
}: VenueMapProps) {
  const [active, setActive] = useState<number | null>(null);

  return (
    <section className="venue-map">
      <p className="venue-map__eyebrow">— {eyebrow}</p>
      <h3 className="venue-map__title">{title}</h3>

      <div className="venue-map__frame">
        <div className="venue-map__canvas">
          <img className="venue-map__img" src={imageSrc} alt={imageAlt} />
        </div>
        {pins.map((pin, i) => {
          const label =
            legend.find((l) => l.num === pin.num)?.label ?? `Location ${pin.num}`;
          return (
            <button
              key={i}
              type="button"
              className={`venue-map__pin${active === pin.num ? ' is-active' : ''}`}
              style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
              aria-label={label}
              onMouseEnter={() => setActive(pin.num)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(pin.num)}
              onBlur={() => setActive(null)}
            >
              <span className="venue-map__pin-chip">{pin.num}</span>
              <span className="venue-map__pin-tip" role="tooltip">{label}</span>
            </button>
          );
        })}
      </div>

      <ol className="venue-map__legend">
        {legend.map((item) => (
          <li
            key={`${item.num}-${item.label}`}
            className={`venue-map__legend-item${active === item.num ? ' is-active' : ''}`}
            onMouseEnter={() => setActive(item.num)}
            onMouseLeave={() => setActive(null)}
          >
            <span className="venue-map__legend-num">{item.num}</span>
            <span className="venue-map__legend-text">
              <span className="venue-map__legend-label">{item.label}</span>
              {item.detail && (
                <span className="venue-map__legend-detail">{item.detail}</span>
              )}
            </span>
          </li>
        ))}
      </ol>

      <style>{`
        .venue-map { margin: 0 0 2.5rem; }
        .venue-map__eyebrow {
          font-family: 'Oswald', Arial, sans-serif;
          font-weight: 500; text-transform: uppercase;
          letter-spacing: 0.35em; color: ${WIC_RED};
          font-size: 0.75rem; margin: 0 0 0.25rem;
        }
        .venue-map__title {
          font-family: 'Oswald', Arial, sans-serif;
          font-weight: 600; text-transform: uppercase;
          font-size: 1.5rem; color: var(--black, #111111);
          margin: 0 0 1rem; line-height: 1.1;
        }
        .venue-map__frame { position: relative; display: block; }
        .venue-map__canvas {
          border: 0.5px solid var(--border, #E2E2E2);
          border-radius: var(--radius, 2px); overflow: hidden;
          background: var(--bg, #F7F6F4);
        }
        .venue-map__img { display: block; width: 100%; height: auto; }
        .venue-map__pin {
          position: absolute; transform: translate(-50%, -100%);
          background: none; border: 0; padding: 0; cursor: pointer;
          z-index: 1;
          /* the translate puts the pin TIP at (x, y) */
        }
        .venue-map__pin:hover,
        .venue-map__pin:focus-visible,
        .venue-map__pin.is-active { z-index: 6; }
        .venue-map__pin-chip {
          position: relative; display: flex;
          align-items: center; justify-content: center;
          width: 32px; height: 32px;
          background: ${WIC_RED}; color: #fff;
          font-family: 'Oswald', Arial, sans-serif;
          font-weight: 600; font-size: 1rem;
          border-radius: var(--radius, 2px);
          box-shadow: 0 1px 3px rgba(0,0,0,0.35);
          transition: transform 0.12s ease;
        }
        .venue-map__pin-chip::after {
          content: ''; position: absolute; top: 100%; left: 50%;
          transform: translateX(-50%);
          border: 6px solid transparent;
          border-top-color: ${WIC_RED}; border-bottom: 0;
        }
        .venue-map__pin:hover .venue-map__pin-chip,
        .venue-map__pin:focus-visible .venue-map__pin-chip,
        .venue-map__pin.is-active .venue-map__pin-chip {
          transform: scale(1.15);
        }
        .venue-map__pin:focus-visible { outline: none; }
        .venue-map__pin:focus-visible .venue-map__pin-chip {
          box-shadow: 0 0 0 3px rgba(192,57,43,0.4);
        }
        .venue-map__pin-tip {
          position: absolute; bottom: calc(100% + 8px); left: 50%;
          transform: translateX(-50%) translateY(4px);
          background: var(--black, #111111); color: #fff;
          font-family: 'Source Sans 3', Arial, sans-serif;
          font-weight: 500; font-size: 0.8125rem; line-height: 1.2;
          white-space: nowrap; padding: 0.4rem 0.6rem;
          border-radius: var(--radius, 2px);
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          opacity: 0; visibility: hidden; pointer-events: none;
          transition: opacity 0.12s ease, transform 0.12s ease;
          z-index: 7;
        }
        .venue-map__pin-tip::after {
          content: ''; position: absolute; top: 100%; left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: var(--black, #111111); border-bottom: 0;
        }
        .venue-map__pin:hover .venue-map__pin-tip,
        .venue-map__pin:focus-visible .venue-map__pin-tip,
        .venue-map__pin.is-active .venue-map__pin-tip {
          opacity: 1; visibility: visible;
          transform: translateX(-50%) translateY(0);
        }
        .venue-map__legend {
          list-style: none; margin: 1rem 0 0; padding: 0;
          display: grid; gap: 0.625rem;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        }
        .venue-map__legend-item {
          display: flex; align-items: flex-start; gap: 0.625rem;
          padding: 0.25rem; border-radius: var(--radius, 2px);
          transition: background 0.12s ease;
        }
        .venue-map__legend-item.is-active { background: var(--bg, #F7F6F4); }
        .venue-map__legend-num {
          flex-shrink: 0; width: 26px; height: 26px;
          background: ${WIC_RED}; color: #fff;
          font-family: 'Oswald', Arial, sans-serif;
          font-weight: 600; font-size: 0.9375rem;
          border-radius: var(--radius, 2px);
          display: flex; align-items: center; justify-content: center;
        }
        .venue-map__legend-text { display: flex; flex-direction: column; }
        .venue-map__legend-label {
          font-family: 'Source Sans 3', Arial, sans-serif;
          font-weight: 500; font-size: 0.875rem;
          color: var(--black, #111111);
        }
        .venue-map__legend-detail {
          font-family: 'Source Sans 3', Arial, sans-serif;
          font-size: 0.8125rem; color: var(--mid, #666666);
        }
      `}</style>
    </section>
  );
}
